import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Stethoscope, CheckCircle } from 'lucide-react';
import { authService } from '@/services/auth.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '', referralCode: refCode });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.includes('@')) errs.email = 'Valid email required';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await authService.register({ name: form.name, email: form.email, phone: form.phone, password: form.password, referralCode: form.referralCode || undefined });
      setDone(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Registration failed';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-success" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-gray-800 mb-2">Check Your Email!</h2>
          <p className="text-gray-500 mb-6">We've sent a verification link to <strong>{form.email}</strong>. Please verify to activate your account.</p>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-center">
        <Link to="/" className="flex items-center gap-3 mb-12">
          <Stethoscope className="h-8 w-8 text-white" />
          <span className="font-heading font-bold text-white text-2xl">Milan Medical</span>
        </Link>
        <h2 className="font-heading font-bold text-4xl text-white mb-4">Join Milan Medical</h2>
        <p className="text-primary-100 text-lg mb-8">Create your account and start earning Milan Points on every purchase!</p>
        <div className="space-y-3">
          {['🎁 Welcome bonus on first order', '⭐ Earn 1 point per ₹10 spent', '💊 Prescription management', '🚚 Free delivery on orders ₹500+'].map(b => (
            <div key={b} className="flex items-center gap-2 text-white/90 text-sm">{b}</div>
          ))}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Stethoscope className="h-8 w-8 text-primary-500" />
            <span className="font-heading font-bold text-2xl text-primary-600">Milan Medical</span>
          </div>
          <h1 className="font-heading font-bold text-2xl text-gray-800 mb-1">Create Account</h1>
          <p className="text-gray-500 text-sm mb-6">Start your health journey with us</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="Full Name" placeholder="Rahul Patel" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} error={errors.name} required />
            <Input label="Email Address" type="email" placeholder="you@example.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} error={errors.email} required />
            <Input label="Phone Number (optional)" placeholder="+91XXXXXXXXXX" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            <div className="relative">
              <Input label="Password" type={showPass ? 'text' : 'password'} placeholder="Min. 8 characters" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} error={errors.password} required />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 bottom-3 text-gray-400">
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Input label="Confirm Password" type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} error={errors.confirmPassword} required />
            <Input label="Referral Code (optional)" placeholder="e.g. MMAB12" value={form.referralCode} onChange={e => setForm(p => ({ ...p, referralCode: e.target.value.toUpperCase() }))} />
            <p className="text-xs text-gray-400">By creating an account, you agree to our Terms of Service and Privacy Policy.</p>
            <Button type="submit" className="w-full" loading={loading}>Create Account</Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 font-semibold hover:text-primary-700">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
